import { FunctionComponent } from 'react';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  Tooltip,
} from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { SeasonFormData } from '../../schemas';
import { QualifierZonesCheatSheet } from './QualifierZonesCheatSheet';

type QualifierZonesInputsProps = {
  directName: string;
  playoffName: string;
  secondaryName: string;
  relegatedName: string;
  /** Caminhos dos campos de nome customizado da zona (label editável). */
  directLabelName: string;
  playoffLabelName: string;
  secondaryLabelName: string;
  relegatedLabelName: string;
  errorMessage?: string;
  /** Esconde a seção de exemplos (já mostrada uma vez na tela). */
  hideCheatSheet?: boolean;
};

const PREFIX = 'pages.application_season.components.season_step2.qualifiers';

type ZoneFieldProps = {
  countName: string;
  labelName: string;
  /** Nome padrão da zona (placeholder quando o usuário não customiza). */
  defaultLabel: string;
  help: string;
  color: string;
};

/**
 * Campo de uma zona (nome editável + contagem). Definido no escopo do módulo —
 * NÃO declarar dentro de QualifierZonesInputs, senão o React remonta os inputs
 * a cada render e o cursor sai a cada tecla.
 */
const ZoneField: FunctionComponent<ZoneFieldProps> = ({
  countName,
  labelName,
  defaultLabel,
  help,
  color,
}) => {
  const { register } = useFormContext<SeasonFormData>();

  return (
    <FormControl w="auto">
      <Flex align="center" gap={1.5} mb={1.5}>
        <Box w="10px" h="10px" borderRadius="full" bg={color} flexShrink={0} />
        <Input
          variant="unstyled"
          size="xs"
          fontSize="11px"
          fontWeight="semibold"
          color="gray.700"
          w="96px"
          placeholder={defaultLabel}
          {...register(labelName as never)}
        />
        <Tooltip label={help} hasArrow placement="top" fontSize="xs" maxW="240px">
          <Box display="inline-flex" alignItems="center" color="gray.400" cursor="help">
            <ChakraFeatherIcon name="help-circle" boxSize="12px" />
          </Box>
        </Tooltip>
      </Flex>
      <Input
        type="number"
        min={0}
        size="sm"
        w="68px"
        bg="white"
        {...register(countName as never, { valueAsNumber: true })}
      />
    </FormControl>
  );
};

/**
 * Trio + 1 inputs numéricos (classificação direta, repescagem, secundária,
 * rebaixamento) com nome editável da zona, cor e tooltip explicativo.
 * Deixar o nome vazio mantém o label default (placeholder).
 */
export const QualifierZonesInputs: FunctionComponent<QualifierZonesInputsProps> = ({
  directName,
  playoffName,
  secondaryName,
  relegatedName,
  directLabelName,
  playoffLabelName,
  secondaryLabelName,
  relegatedLabelName,
  errorMessage,
  hideCheatSheet = false,
}) => {
  const { t } = useTranslation();

  return (
    <FormControl isInvalid={!!errorMessage}>
      <Flex gap={4} align="flex-end" wrap="wrap">
        <ZoneField
          countName={directName}
          labelName={directLabelName}
          defaultLabel={t(`${PREFIX}.direct.label`)}
          help={t(`${PREFIX}.direct.help`)}
          color="green.500"
        />
        <ZoneField
          countName={playoffName}
          labelName={playoffLabelName}
          defaultLabel={t(`${PREFIX}.playoff.label`)}
          help={t(`${PREFIX}.playoff.help`)}
          color="blue.500"
        />
        <ZoneField
          countName={secondaryName}
          labelName={secondaryLabelName}
          defaultLabel={t(`${PREFIX}.secondary.label`)}
          help={t(`${PREFIX}.secondary.help`)}
          color="orange.500"
        />
        <ZoneField
          countName={relegatedName}
          labelName={relegatedLabelName}
          defaultLabel={t(`${PREFIX}.relegated.label`)}
          help={t(`${PREFIX}.relegated.help`)}
          color="red.500"
        />
      </Flex>
      {errorMessage ? <FormErrorMessage>{errorMessage}</FormErrorMessage> : null}
      {!hideCheatSheet ? (
        <Box mt={3}>
          <QualifierZonesCheatSheet />
        </Box>
      ) : null}
    </FormControl>
  );
};
